import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { bool, string, object, number, arrayOf, node, oneOfType } from "prop-types";
import { observer } from "mobx-react";
import { BreakdownTableDepths, DescriptionModes, hashCode } from "@repo/utils";
import { BREAKDOWN_ELEMENT_CODES, CELL_HEIGHTS } from "@utils";
import {
  useEstimateEditorSettings,
  useEstimateEditorStore,
  useEstimateStructure,
  RowGroup,
  PlaceholderRow,
} from "@tools";
import { Collapse } from "@material-ui/core";
import { BreakdownRowGroupList } from "../BreakdownRowGroupList";
import { BreakdownNewChildRow } from "./BreakdownNewChildRow";
import { useStores } from "@hooks";
import { useInView } from "react-intersection-observer";

export const BreakdownRowGroupContainer = observer(({
  element,
  children,
  expanded,
  expandable,
  isLastGroupRow,
  hashedPath,
  actualDepthLevel,
  tableDepth = BreakdownTableDepths.SECTION,
  initialDepth = BreakdownTableDepths.SECTION,
  isFirst,
  isExpanding,
  isSectionRow,
  collapseProps,
  hasEditedDescription,
  ...containerProps
}) => {
  const { stateStore } = useStores();
  const editorStore = useEstimateEditorStore();
  const structure = useEstimateStructure();
  const { isPdfPrinterView, allowEdition, previewMode, forceExpansionArrows } = useEstimateEditorSettings();
  
  const [ placeholderHeight, setPlaceholderHeight ] = useState();
  const [ holdsFocus, setHoldsFocus ] = useState();
  
  const ref = useRef(null);
  
  const { descriptionMode } = structure.settings;
  
  const checkContainerHeight = () => {
    const elementHeight = ref.current.children[0]?.offsetHeight || 0;
    const descriptionHeight = hasEditedDescription && (ref.current.children.length >= 3 || !expanded)
      ? ref.current.children[1]?.offsetHeight || 0
      : 0;
    
    const height = elementHeight + descriptionHeight;
    if(height && height !== placeholderHeight)
      setPlaceholderHeight(height);
  }
  
  const [ inViewRef, loadContent ] = useInView({
    root: stateStore.scrollRef,
    skip: previewMode || isPdfPrinterView,
    initialInView: previewMode || isPdfPrinterView || editorStore.isAiGenerating || isExpanding,
    rootMargin: `${20*CELL_HEIGHTS.LARGE}px 0px`,
    onChange: (value) => {
      if(!value) {
        setHoldsFocus(Boolean(ref.current?.querySelector(":focus")));
      }
      if(!value && !isExpanding)
        checkContainerHeight();
    }
  });
  
  useLayoutEffect(() => {
    if(loadContent && !isExpanding)
      checkContainerHeight();
  }, [loadContent]);
  
  const setRefs = useCallback((node) => {
    ref.current = node;
    inViewRef(node);
  }, [inViewRef]);
  
  useEffect(() => {
    if(ref.current && editorStore.displayedComment && element.treePath.join("/") === editorStore.displayedComment.path) {
      // const top = ref.current.getBoundingClientRect().y - 128;
      const scrollRef = stateStore.useAuxiliaryScrollRef ? stateStore.auxiliaryScrollRef : stateStore.scrollRef
      const containerRect = ref.current.getBoundingClientRect();
      const top = Math.round(containerRect.top) + scrollRef.scrollTop + ref.current.children[0].offsetHeight - document.body.offsetHeight/2;
      // const top = ref.current.offsetTop + ref.current.children[0].offsetHeight - document.body.offsetHeight/2
      if(top > 0)
        scrollRef?.scrollTo({ top, behavior: "smooth" });
    }
  }, [editorStore.displayedComment, ref.current]);

  const { isExpanding: isRowExpanding, ...toCollapse } = collapseProps;
  
  const avoidPageBreaks = useMemo(() => (
    expanded && isPdfPrinterView && actualDepthLevel === BreakdownTableDepths.MODULE && element.allVisibleDescendants <= 4
  ), [ expanded, isPdfPrinterView, actualDepthLevel, element.allVisibleDescendants ]);
  
  return (
    <RowGroup
      ref={setRefs}
      tableDepth={tableDepth}
      isActive={expanded}
      isStatic={!expandable && !(descriptionMode === DescriptionModes.COLUMN && element.hasAnyDescriptions) && !forceExpansionArrows}
      hashedPath={hashedPath}
      elementType={BREAKDOWN_ELEMENT_CODES[actualDepthLevel]}
      avoidPageBreaks={avoidPageBreaks}
      { ...containerProps }
    >
      {
        loadContent || holdsFocus
          ? children
          : <PlaceholderRow
            expanded={expanded}
            isFirstGroupRow={tableDepth <= BreakdownTableDepths.MODULE}
            isLastGroupRow={
              isSectionRow || (isLastGroupRow && !expanded && !hasEditedDescription)
            }
            tableDepth={tableDepth}
            height={placeholderHeight}
            style={!tableDepth ? { marginBottom: 2 } : undefined}
            actionPresetClass="preset-page"
          />
      }
      {
        expandable &&
        <Collapse
          in={expanded}
          className="w-full"
          mountOnEnter
          unmountOnExit
          data-id={hashCode(`${element.id}_cont`)}
          data-p={hashedPath.join("/")}
          data-t={BREAKDOWN_ELEMENT_CODES[actualDepthLevel + 1]}
          {...toCollapse}
        >
          <BreakdownRowGroupList
            element={element}
            hashedPath={hashedPath}
            tableDepth={tableDepth}
            initialDepth={initialDepth}
            isLastGroupRow={
              tableDepth === 0 || (isLastGroupRow && !allowEdition)
            }
            parentName={element.name}
            disabled={!allowEdition}
            isExpanding={isRowExpanding}
          />
          {
            allowEdition &&
            <BreakdownNewChildRow
              element={element}
              tableDepth={tableDepth}
              actualDepthLevel={actualDepthLevel}
              isFirst={isFirst}
            />
          }
        </Collapse>
      }
    </RowGroup>
  );
})

BreakdownRowGroupContainer.propTypes = {
  element: object.isRequired,
  children: node.isRequired,
  collapseProps: object.isRequired,
  hashedPath: arrayOf(oneOfType([number, string])).isRequired,
  tableDepth: number,
  initialDepth: number,
  isFirst: bool,
  isLastGroupRow: bool,
  isExpanding: bool,
  
  expanded: bool,
  expandable: bool,
  actualDepthLevel: number,
  isSectionRow: bool,
  hasEditedDescription: bool,
};
